import _ from 'lodash';
import React from 'react';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useAccountAPI } from '../../apis/account-api';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { layoutDefaults } from '../../utils/layout-defaults';
import { useAnalytics } from '../../utils/setup/analytics-setup';
import { useUpdateProfileOkModal } from './update-profile-ok-modal';

function useUpdateProfileModal() {
  const { analyticsPageView } = useAnalytics();
  const { user, setUser } = useStorage();
  const { t } = useTranslation();
  const storage = useStorage();
  const showUpdateProfileOkModal = useUpdateProfileOkModal();
  const { handleErrorOnModal } = useErrorHandler();
  const { swal } = useSwal({ storage });
  const { updateProfileNameFromRemote } = useAccountAPI();
  const classes = useStyles();

  function didOpen() {
    analyticsPageView('Update Profile');
  }

  async function logout() {
    // eslint-disable-next-line no-alert, no-undef
    if (window.confirm(t('update-profile-modal.question.confirmation'))) {
      setUser(null);
      swal.close();
    }
  }

  async function show() {
    const result = await swal.fire({
      icon: 'question',
      title: t('update-profile-modal.question.name'),
      input: 'text',
      didOpen,
      // didClose: resolve,
      preConfirm,
      showLoaderOnConfirm: true,
      inputPlaceholder: user.name,
      inputAttributes: { autocapitalize: 'on', maxlength: 30 },
      confirmButtonText: t('save'),
      cancelButtonText: t('cancel'),
      allowOutsideClick: true,
      showCloseButton: true,
      showCancelButton: true,
      showClass: {
        popup: 'animate__animated animate__slideInLeft animate__faster',
      },
      footer: (
        <div className={classes.linkContainer}>
          <Button
            variant="link"
            onClick={() => logout()}
            className={classes.link}
          >
            {t('update-profile-modal.sign-out')}
          </Button>
        </div>
      ),
    });

    if (result.isConfirmed) {
      await showUpdateProfileOkModal(result.value);
      return result.value;
    }

    return result;
  }

  async function preConfirm(data) {
    if (_.isEmpty(data)) {
      return swal.showValidationMessage(t('update-profile-modal.request.name'));
    }

    try {
      const response = await updateProfileNameFromRemote(data);
      const name = response.data?.name ?? data;
      setUser({ ...user, name });
      return name;
    } catch (error) {
      return handleErrorOnModal(error);
    }
  }

  return {
    showUpdateProfileModal: show,
  };
}

const useStyles = createUseStyles({
  linkContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  link: {
    cursor: 'pointer',
    textAlign: 'center',
    fontSize: layoutDefaults.fontSize,
    color: layoutDefaults.linkColor,
    paddingRight: layoutDefaults.horizontalMargin * 2,
    paddingBottom: 0,
    // border: "1px solid red",
  },
});

export { useUpdateProfileModal };
